// PostHog Cloud (EU) — product analytics + session replay pour donoor.org.
//
// Pourquoi PostHog plutôt que GA seul : events métier précis (funnel de don),
// session replay, funnels natifs, et surtout ingestion via un reverse-proxy
// 1st-party (`/ingest`, cf. vercel.json) qui traverse les adblockers là où
// GA/Amplitude sont bloqués.
//
// RGPD — le tracking est OPT-IN strict :
//   - init en `opt_out` : AUCUN event, AUCUN cookie tant que l'utilisateur
//     n'a pas accepté les cookies analytics via le bandeau (CookieConsentBanner).
//   - app.vue écoute `update:consent` et appelle posthog.opt_in_capturing()
//     / opt_out_capturing() selon le choix. Source unique : `donoor-cookie-consent`.
//
// Perf (aligné hotjar/gtag) : init différée à l'idle via requestIdleCallback
// pour ne pas concurrencer le LCP/INP. Les appels track() faits avant l'init
// sont bufferisés par le SDK (queue interne) — aucun event perdu.
//
// Session replay : masquage agressif (tous les inputs + champs sensibles) —
// on capture le parcours/UX, jamais une donnée perso ou financière.
//
// Bundle (audit perf 2026-07-22) : posthog-js est importé DYNAMIQUEMENT dans
// le callback idle — l'import statique le mettait dans le chunk d'entrée
// (~150 Ko min) chargé sur toutes les pages avant interaction. On expose une
// façade synchrone (mêmes méthodes, no-op tant que le SDK n'est pas chargé)
// pour que app.vue puisse destructurer $posthog au setup comme avant.

import type { PostHog } from "posthog-js";

export default defineNuxtPlugin((nuxtApp) => {
  const config = useRuntimeConfig();
  const key = (config.public.posthogKey as string | undefined) || "";
  const host =
    (config.public.posthogHost as string | undefined) || "/ingest";

  // Pas de clé (staging/dev) → skip total, provide un no-op pour les consumers.
  if (!key) {
    nuxtApp.provide("posthog", null);
    return;
  }

  // Prod (fuite d'env bloquée sur preview/staging) OU dev local explicite
  // (pour tester : renseigner NUXT_PUBLIC_POSTHOG_KEY + POSTHOG_HOST direct EU
  // dans .env, cf. .env.example). Les previews Vercel restent exclus.
  const hostname = window.location.hostname;
  const isProdHost =
    hostname === "donoor.org" || hostname === "www.donoor.org";
  if (!isProdHost && !import.meta.dev) {
    nuxtApp.provide("posthog", null);
    return;
  }

  // Résout le host : "/ingest" (proxy 1st-party) → URL absolue même origine.
  const apiHost = host.startsWith("/")
    ? `${window.location.origin}${host}`
    : host;

  // Capturé MAINTENANT (contexte Nuxt synchrone) : dans le callback différé,
  // useRouter ne serait plus disponible.
  const router = useRouter();

  // Init après le premier rendu complet (window.load) puis à l'idle : l'ancien
  // timeout 3s se déclenchait en pleine fenêtre critique LCP sur mobile lent.
  // Les track() faits avant sont bufferisés par le SDK — aucun event perdu.
  const deferInit = (init: () => void) => {
    const schedule = () => {
      if ("requestIdleCallback" in window) {
        (
          window as Window & {
            requestIdleCallback: (
              cb: () => void,
              opts?: { timeout: number },
            ) => void;
          }
        ).requestIdleCallback(init, { timeout: 6000 });
      } else {
        setTimeout(init, 4000);
      }
    };
    if (document.readyState === "complete") {
      schedule();
    } else {
      window.addEventListener("load", schedule, { once: true });
    }
  };

  // Façade synchrone : provide immédiat (app.vue destructure au setup),
  // délégation vers le vrai SDK une fois l'import dynamique résolu.
  // `__loaded` reste false tant que le SDK n'est pas init — app.vue le
  // vérifie déjà avant tout appel, et le consentement pré-chargement est
  // relu depuis localStorage dans le callback `loaded` ci-dessous.
  let real: PostHog | null = null;
  const facade = {
    get __loaded() {
      return real?.__loaded ?? false;
    },
    opt_in_capturing: () => real?.opt_in_capturing(),
    opt_out_capturing: () => real?.opt_out_capturing(),
    capture: (...args: Parameters<PostHog["capture"]>) =>
      real?.capture(...args),
    identify: (...args: Parameters<PostHog["identify"]>) =>
      real?.identify(...args),
    reset: () => real?.reset(),
  } as unknown as PostHog;

  deferInit(async () => {
    const { default: posthog } = await import("posthog-js");
    real = posthog;
    posthog.init(key, {
      api_host: apiHost,
      // Le proxy 1st-party sert aussi les assets statiques du SDK.
      ui_host: "https://eu.posthog.com",
      // Data en Europe (RGPD).
      // ─── Consentement (opt-in strict) ───────────────────────────────────
      // Ne rien capturer tant que le bandeau n'a pas accordé le consentement
      // analytics. app.vue déclenche opt_in_capturing() au consentement.
      opt_out_capturing_by_default: true,
      // Crée les profils "person" uniquement pour les users identifiés
      // (identify) — les visiteurs anonymes ne consomment pas de quota person.
      person_profiles: "identified_only",
      // Pas de capture auto des pageviews : on pilote via useAnalytics
      // (trackPageView) pour rester cohérent avec GA/Amplitude et éviter le
      // double comptage. `capture_pageleave` reste utile pour la durée.
      capture_pageview: false,
      capture_pageleave: true,
      // Autocapture des interactions frontend (clics, submits) + heatmaps +
      // web vitals (LCP/INP/CLS). Complète les events métier sans code.
      // Ne consomme pas de quota "person" (person_profiles: identified_only).
      autocapture: true,
      capture_heatmaps: true,
      capture_performance: { web_vitals: true },
      // ─── Modules du SDK qui ne servaient à rien ─────────────────────────
      // Audit perf 2026-09-14 : le SDK téléchargeait, sur chaque page
      // publique, deux modules que Donoor n'exploite pas —
      //   surveys.js              34 Ko (dont 28 Ko de code jamais exécuté)
      //   dead-clicks-autocapture 9,4 Ko
      // Les sondages PostHog ne sont utilisés nulle part (aucun sondage créé),
      // et le « dead click » est un signal de diagnostic qu'on ne regarde pas.
      // Remettre `disable_surveys: false` le jour où un sondage est lancé.
      disable_surveys: true,
      capture_dead_clicks: false,
      // ─── Session replay ─────────────────────────────────────────────────
      disable_session_recording: false,
      session_recording: {
        // Masque TOUS les champs de saisie (montants de don, email, etc.).
        maskAllInputs: true,
        // Masque aussi le texte des sélecteurs sensibles (montants affichés,
        // iframes Stripe/PayPal sont déjà cross-origin donc non capturées).
        maskTextSelector:
          "[data-ph-mask], .donation-amount, [data-amount], .amount",
      },
      // Respecte le Do Not Track du navigateur.
      respect_dnt: true,
      loaded: (ph) => {
        // Filet : si un consentement analytics est déjà stocké (retour d'un
        // visiteur qui avait accepté), on opt-in immédiatement sans attendre
        // le bandeau. Lecture directe du même localStorage que le bandeau.
        try {
          const raw = localStorage.getItem("donoor-cookie-consent");
          if (raw) {
            const parsed = JSON.parse(raw) as { analytics?: boolean };
            if (parsed?.analytics === true) ph.opt_in_capturing();
          }
        } catch {
          // ignore
        }
        // $pageview manuel (capture_pageview: false — aucun autre émetteur
        // dans l'app) : la vue d'arrivée. No-op tant que le visiteur n'a pas
        // opté in (opt_out_capturing_by_default), donc RGPD-safe.
        ph.capture("$pageview");
      },
    });

    // $pageview à chaque navigation SPA. posthog.capture est no-op si le
    // visiteur n'a pas consenti — aucune fuite hors opt-in.
    router.afterEach(() => {
      void nextTick(() => posthog.capture("$pageview"));
    });
  });

  nuxtApp.provide("posthog", facade);
});
